/**
 * Dates and instants (TMMPDM-0065, TMM-B1 / TMM-L4). A date-only value ("2026-09-01") is built
 * from its parts, never `new Date('YYYY-MM-DD')` — that parses as UTC midnight and shows the
 * previous day west of UTC. An instant carries a time. "—" is a value the API didn't send.
 */
import { NOT_SENT, type Maybe } from './money';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseIsoDate(value: string): Date | null {
  const dateOnly = DATE_ONLY.exec(value);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** A date-only value (or an ISO string, read as the day it falls on) in the locale; the raw value if unparseable. */
export function formatDate(value: Maybe<string>, locale: string): string {
  if (!value) return NOT_SENT;
  const parsed = parseIsoDate(value);
  if (parsed === null) return value;
  return parsed.toLocaleDateString(locale);
}

/**
 * A month and year ("September 2026"): a settlement period, never "this month". A date-only
 * value is read in UTC, so a month's 1st never shows as the month before.
 */
export function formatMonthYear(value: Maybe<string>, locale: string): string {
  if (!value) return NOT_SENT;
  const dateOnly = DATE_ONLY.exec(value);
  if (dateOnly) {
    const date = new Date(Date.UTC(Number(dateOnly[1]), Number(dateOnly[2]) - 1, 1));
    return new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }
  const parsed = parseIsoDate(value);
  if (parsed === null) return value;
  return parsed.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

/** An instant: date and time in the locale, or just its date with `part: 'date'`; the raw value if unparseable. */
export function formatInstant(
  value: Maybe<string>,
  locale: string,
  part: 'datetime' | 'date' = 'datetime',
): string {
  if (!value) return NOT_SENT;
  const parsed = parseIsoDate(value);
  if (parsed === null) return value;
  return part === 'date' ? parsed.toLocaleDateString(locale) : parsed.toLocaleString(locale);
}

/** A clock time (hours and minutes) from a client-side `Date`, e.g. "Loaded at 10:42". */
export function formatTime(date: Date, locale: string): string {
  return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/**
 * `YYYY-MM-DD HH:mm UTC` (TMMPDM-0088): an effective-dated setting's instant in UTC, whatever the
 * browser's zone, so what is picked is what is seen. The ISO form, not a locale form.
 */
export function formatUtcMinute(instant: string): string {
  const ms = Date.parse(instant);
  if (Number.isNaN(ms)) return instant;
  const iso = new Date(ms).toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}
